import {useEffect, useMemo, useRef} from 'react';
import Stack from '@mui/material/Stack';
import {useTranslation} from 'react-i18next';

import {
    OTHER_RECIPIENT,
    buildProfileRecipientOptions,
    formatProfileRecipient,
    matchRecipientOption
} from '../../../config/recipients';
import {buildAvailableVariables} from '../../../config/nodeVariables';
import {guaranteedUpstreamNodes} from '../../../config/reachability';
import AutomationTextField from '../variables/AutomationTextField';
import NodeEditorLayout, {NodeEditorState} from '../NodeEditorLayout';
import {SingleSelect} from '../../../../../common/newSelect';
import {NodeEditorV2Props} from '../types';

const SendPushEditorV2 = (
    {nodeId, nodeConfig, setNodeConfig, upstreamNodes = [], flowNodes, flowConnections, readOnly}: NodeEditorV2Props
) => {
    const {t} = useTranslation();
    const availableVariables = useMemo(() => buildAvailableVariables(upstreamNodes), [upstreamNodes]);
    // Only steps that run on *every* path here — see guaranteedUpstreamNodes.
    const recipientSources = useMemo(
        () => guaranteedUpstreamNodes(upstreamNodes, flowNodes, flowConnections, nodeId),
        [upstreamNodes, flowNodes, flowConnections, nodeId]
    );
    const recipientOptions = useMemo(() => buildProfileRecipientOptions(recipientSources, t), [recipientSources, t]);
    // Push reads only profileId. An old, unused email key must not affect its selected recipient.
    const selectedRecipient = matchRecipientOption({profileId: nodeConfig.profileId}, recipientOptions);
    const recipient = formatProfileRecipient(nodeConfig.profileId, t);
    const title: string = nodeConfig.title || '';
    const message: string = nodeConfig.message || '';

    const seededRef = useRef(false);
    useEffect(() => {
        if (seededRef.current || readOnly) return;
        if (nodeConfig.profileId) {
            seededRef.current = true;
            return;
        }
        if (!recipientOptions.length) return;

        seededRef.current = true;
        setNodeConfig({...nodeConfig, profileId: recipientOptions[0].profileId});
    }, [nodeConfig, recipientOptions, readOnly, setNodeConfig]);

    const write = (patch: Record<string, any>) => setNodeConfig({...nodeConfig, ...patch});

    const handleRecipient = (value: string) => {
        seededRef.current = true;
        const option = recipientOptions.find(entry => entry.value === value);
        write({profileId: option?.profileId});
    };

    const {state, summary}: {state: NodeEditorState; summary: string} = (() => {
        if (!recipient) {
            return {
                state: 'wontRun',
                summary: recipientOptions.length
                    ? t('No recipient yet, pick who this notification goes to.')
                    : t('No earlier step supplies a guest profile. Enter profile ids, or connect an Audience or guest trigger before this step.')
            };
        }

        // The executor accepts empty content: warn (caution), never block the save.
        if (!title.trim() || !message.trim()) {
            return {
                state: 'caution',
                summary: t('Add a title and message so guests can understand the notification.')
            };
        }

        return {
            state: 'configured',
            summary: t('Sends ‘{{title}}’ to {{recipient}}. Only guests with push notifications available in the guest app can receive it.', {title, recipient})
        };
    })();

    return (
        <NodeEditorLayout state={state} summary={summary}>
            <Stack gap={1}>
                <SingleSelect
                    label={t('Recipient')}
                    value={selectedRecipient}
                    onChange={handleRecipient}
                    disabled={readOnly}
                    disableClearable
                    options={[
                        ...recipientOptions.map(option => ({value: option.value, label: option.label})),
                        {value: OTHER_RECIPIENT, label: t('Someone else')}
                    ]}
                />

                {selectedRecipient === OTHER_RECIPIENT && (
                    <AutomationTextField
                        insertion={'step-value'}
                        disabled={readOnly}
                        label={t('Profile ids')}
                        value={nodeConfig.profileId || ''}
                        onChange={value => write({profileId: value})}
                        helperText={t('Separate profile ids with commas. Type / to choose a value from an earlier step.')}
                        variables={availableVariables}
                    />
                )}
            </Stack>

            <AutomationTextField
                insertion={'step-value'}
                disabled={readOnly}
                label={t('Title')}
                value={title}
                onChange={value => write({title: value})}
                variables={availableVariables}
            />

            <AutomationTextField
                insertion={'step-value'}
                disabled={readOnly}
                label={t('Message')}
                value={message}
                onChange={value => write({message: value})}
                multiline
                rows={3}
                helperText={t('Type / to insert a value from an earlier step.')}
                variables={availableVariables}
            />

            <AutomationTextField
                insertion={'step-value'}
                disabled={readOnly}
                label={t('Open when tapped (optional)')}
                value={nodeConfig.deeplink || ''}
                onChange={value => write({deeplink: value})}
                helperText={t('A deep link to the guest app screen to open when the notification is tapped.')}
                variables={availableVariables}
            />
        </NodeEditorLayout>
    );
};

export default SendPushEditorV2;
